import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import { East, West } from '@mui/icons-material';

import './Home.css';
import './MyBuilds.css';
import '../components/aiChat/AiChatPanel.css';
import { useAuth } from '../contexts/AuthContext';
import { NOTIFICATION, useNotification } from '../components/NotificationSystem';
import { interpretAiDraft, createAiBuildClaim } from '../services/AiChatService';
import { createRemoteBuild } from '../services/buildApi';
import { buildDiscordLoginUrl } from '../services/authApi';
import * as buildStorage from '../services/buildStorage';
import { saveConversation, loadConversation } from '../services/chatHistoryStorage';
import { aiDraftToBuild } from '../utils/aiDraftToBuild';
import GenConfig from '../core/GenConfig';
import { aiDraftToSavedBuildPayload } from '../utils/aiDraftToSavedBuildPayload';
import { suggestAiBuildName } from '../utils/suggestAiBuildName';
import { applySolverBuildPayload } from '../utils/applySolverBuildPayload';
import {
    serializeBuildForTranscript,
    serializeBuildOpenedMarker,
} from '../utils/buildTranscriptSummary';
import { canonicalBuildPath, localGenPath } from '../utils/buildRoutes';
import { isLocalDevEnvironment } from '../utils/isLocalDev';
import { useGeneratorContext } from '../contexts/GeneratorStateContext';
import { useGenerationJob } from '../hooks/useGenerationJob';
import { solveBuild } from '../services/GenService';
import { useAiChatScroll } from '../hooks/useAiChatScroll';
import {
    t,
    draftTimeLimit,
    summarizeDraftText,
    enrichChatSiteLinks,
    extractChatCtas,
    AI_CHAT_GENERATION_TIME_LIMIT_FALLBACK,
} from '../components/aiChat/aiChatUtils';
import AiChatBuildCard from '../components/aiChat/AiChatBuildCard';
import AiChatDraftChips from '../components/aiChat/AiChatDraftChips';

const MAX_TRANSCRIPT_MESSAGES_TO_SEND = 16;

const CHAT_INLINE_LINK_CLASS = 'ai-chat-inline-link';

function MarkdownMessage({ content, lang = 'fr' }) {
    if (!content) return null;
    const { text: ctaText, ctas } = extractChatCtas(content);
    const linked = enrichChatSiteLinks(ctaText, lang);
    // Gemini sometimes emits list items on one line: "text. * Item2. * Item3"
    // Normalize ". * " (sentence-end + bullet) into actual newline-separated bullets.
    // The lookahead ensures we only split when * is followed by a space then non-* content
    // (i.e. a real bullet), not when it's part of *italic* or **bold**.
    const normalized = linked.replace(/([.!?])\s*\*\s+(?=[^*\n]|\*\*)/g, '$1\n* ');
    const lines = normalized.split('\n');
    const elements = [];
    let listItems = [];

    const renderInline = (text, key) => {
        const parts = [];
        const re = /\[([^\]]+)\]\(([^)]+)\)|\*\*(.+?)\*\*|\*(.+?)\*/g;
        let last = 0;
        let m;
        let partIdx = 0;
        while ((m = re.exec(text)) !== null) {
            if (m.index > last) parts.push(text.slice(last, m.index));
            const partKey = `${key}-${partIdx}`;
            if (m[1] !== undefined && m[2] !== undefined) {
                const href = m[2];
                const label = m[1];
                if (href.startsWith('/')) {
                    parts.push(
                        <Link key={partKey} to={href} className={CHAT_INLINE_LINK_CLASS}>
                            {label}
                        </Link>
                    );
                } else if (/^https?:\/\//i.test(href)) {
                    parts.push(
                        <a
                            key={partKey}
                            href={href}
                            className={CHAT_INLINE_LINK_CLASS}
                            target="_blank"
                            rel="noopener noreferrer"
                        >
                            {label}
                        </a>
                    );
                } else {
                    parts.push(label);
                }
            } else if (m[3] !== undefined) {
                parts.push(<strong key={partKey}>{m[3]}</strong>);
            } else if (m[4] !== undefined) {
                parts.push(<em key={partKey}>{m[4]}</em>);
            }
            partIdx += 1;
            last = re.lastIndex;
        }
        if (last < text.length) parts.push(text.slice(last));
        return <span key={key}>{parts}</span>;
    };

    const flushList = () => {
        if (listItems.length) {
            elements.push(<ul key={`ul${elements.length}`}>{listItems}</ul>);
            listItems = [];
        }
    };

    lines.forEach((line, i) => {
        const bullet = line.match(/^[*-]\s+(.*)/);
        if (bullet) {
            listItems.push(<li key={i}>{renderInline(bullet[1], i)}</li>);
        } else {
            flushList();
            if (line.trim()) elements.push(<p key={i}>{renderInline(line, i)}</p>);
        }
    });
    flushList();

    return (
        <>
            {elements}
            {ctas.length > 0 && (
                <div className="ai-chat-ctas">
                    {ctas.map((cta) => (
                        <Link key={`${cta.to}-${cta.label}`} to={cta.to} className="ai-chat-cta-link">
                            <button type="button" className="primary alt">{cta.label}</button>
                        </Link>
                    ))}
                </div>
            )}
        </>
    );
}

// NOTE: Mounted with key={conversationId} from the route, so any change to the
// conversation id causes a clean remount. That keeps sendPrompt's closure over
// `transcript` correct in the mount-time initial-prompt path below.
const Chat = ({ lang = 'fr', conversationId }) => {
    const navigate = useNavigate();
    const location = useLocation();
    const { showNotification } = useNotification();
    const { isAuthenticated } = useAuth();
    const { preApplyRemoteEntry } = useGeneratorContext();
    const generationJob = useGenerationJob();
    const [messages, setMessages] = useState([]);
    const [transcript, setTranscript] = useState([]);
    const [input, setInput] = useState('');
    const [interpreting, setInterpreting] = useState(false);
    const [activeDraftMessageId, setActiveDraftMessageId] = useState(null);
    const [generatedState, setGeneratedState] = useState(null);
    const [openBusy, setOpenBusy] = useState(false);

    const baseBuildRef = useRef(null);
    const inputRef = useRef(null);
    const conversationIdRef = useRef(conversationId);
    const hydratedRef = useRef(false);
    const { pinToBottom } = useAiChatScroll(messages, interpreting);

    useEffect(() => {
        inputRef.current?.focus();
    }, []);

    const activeDraft = useMemo(() => {
        if (!activeDraftMessageId) return null;
        const msg = messages.find((m) => m.id === activeDraftMessageId);
        return msg?.draft || null;
    }, [activeDraftMessageId, messages]);

    // Persist conversation whenever it changes.
    useEffect(() => {
        if (!conversationIdRef.current || messages.length === 0) return;
        saveConversation({ id: conversationIdRef.current, messages, transcript, lang });
    }, [messages, transcript, lang]);

    // Hydrate from storage on mount, or consume the seed prompt from location state.
    // Empty deps: the component is remounted (via route `key`) whenever conversationId changes.
    // hydratedRef guards against React 18 StrictMode's intentional double-invocation
    // of mount effects in dev, which would otherwise send the initial prompt twice.
    useEffect(() => {
        if (hydratedRef.current) return;
        hydratedRef.current = true;

        const stored = loadConversation(conversationId);
        if (stored) {
            setMessages(stored.messages || []);
            setTranscript(stored.transcript || []);
            const lastDraft = [...(stored.messages || [])].reverse().find((m) => m.kind === 'draft');
            setActiveDraftMessageId(lastDraft?.id || null);
            requestAnimationFrame(() => pinToBottom());
            return;
        }
        const initialPrompt = location.state?.initialPrompt;
        if (initialPrompt) {
            sendPrompt(initialPrompt);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const addMessage = (msg) => {
        setMessages((prev) => [
            ...prev,
            {
                id: `${Date.now()}-${Math.random().toString(16).slice(2)}`,
                ...msg,
            },
        ]);
    };

    const updateActiveDraft = (updater) => {
        const draftId = activeDraftMessageId;
        if (!draftId) return;
        setMessages((msgs) => msgs.map((m) => {
            if (m.id !== draftId || !m.draft) return m;
            const next = updater(m.draft);
            if (!next || next === m.draft) return m;
            return { ...m, draft: next };
        }));
    };

    const setDraftLevel = (lvl) => {
        const cleanLvl = Math.max(1, Math.min(200, Math.trunc(Number(lvl) || 0)));
        updateActiveDraft((d) => ({
            ...d,
            req: { ...(d.req || {}), lvl: cleanLvl },
        }));
    };

    const setDraftTimeLimit = (val) => {
        const clean = GenConfig.snapTimeLimit(val);
        updateActiveDraft((d) => ({
            ...d,
            req: { ...(d.req || {}), time_limit: clean },
        }));
    };

    const setDraftGenConfig = (genConfigValue) => {
        const nextConfig = genConfigValue instanceof GenConfig
            ? genConfigValue
            : new GenConfig(genConfigValue || {});
        updateActiveDraft((d) => ({
            ...d,
            req: {
                ...(d.req || {}),
                time_limit: nextConfig.timeLimit,
                defense_share: nextConfig.defenseShare,
                settings: nextConfig.getSettings(),
            },
        }));
    };

    const setDraftExo = (statId, value) => {
        const id = Number(statId);
        const cleanVal = Math.max(0, Math.trunc(Number(value) || 0));
        updateActiveDraft((d) => ({
            ...d,
            req: {
                ...(d.req || {}),
                exos: { ...(d.req?.exos || {}), [id]: cleanVal },
            },
        }));
    };

    const setDraftStatValue = (statId, value) => {
        const cleanVal = Math.max(0, Math.trunc(Number(value) || 0));
        updateActiveDraft((d) => {
            const labels = d.draft_metadata?.criteria_labels?.stats || [];
            return {
                ...d,
                req: {
                    ...(d.req || {}),
                    stats_criteria: {
                        ...(d.req?.stats_criteria || {}),
                        [statId]: { ...((d.req?.stats_criteria || {})[statId] || {}), min: cleanVal },
                    },
                },
                draft_metadata: {
                    ...(d.draft_metadata || {}),
                    criteria_labels: {
                        ...(d.draft_metadata?.criteria_labels || {}),
                        stats: labels.map((row) => (row.id === statId ? { ...row, value: cleanVal } : row)),
                    },
                },
            };
        });
    };

    const removeDraftStat = (statId) => {
        const id = Number(statId);
        updateActiveDraft((d) => {
            const labels = d.draft_metadata?.criteria_labels?.stats || [];
            const nextStatsCriteria = { ...(d.req?.stats_criteria || {}) };
            delete nextStatsCriteria[id];
            delete nextStatsCriteria[String(id)];
            return {
                ...d,
                req: { ...(d.req || {}), stats_criteria: nextStatsCriteria },
                draft_metadata: {
                    ...(d.draft_metadata || {}),
                    criteria_labels: {
                        ...(d.draft_metadata?.criteria_labels || {}),
                        stats: labels.filter((row) => Number(row.id) !== id),
                    },
                },
            };
        });
    };

    const removeDraftSpell = (spellId) => {
        const id = Number(spellId);
        const entrySpellId = (entry) => Number(
            entry && typeof entry === 'object'
                ? (entry.spell_id ?? entry.spellId ?? entry.id)
                : entry
        );
        updateActiveDraft((d) => {
            const labels = d.draft_metadata?.criteria_labels?.spells || [];
            const criteria = Array.isArray(d.req?.spells_criteria) ? d.req.spells_criteria : [];
            return {
                ...d,
                req: {
                    ...(d.req || {}),
                    spells_criteria: criteria.filter((entry) => entrySpellId(entry) !== id),
                },
                draft_metadata: {
                    ...(d.draft_metadata || {}),
                    criteria_labels: {
                        ...(d.draft_metadata?.criteria_labels || {}),
                        spells: labels.filter((row) => entrySpellId(row) !== id),
                    },
                },
            };
        });
    };

    const sendPrompt = async (rawPrompt) => {
        const prompt = String(rawPrompt || '').trim();
        if (!prompt || interpreting || generationJob.isGenerating) return;

        const nextTranscript = [...transcript, { role: 'user', content: prompt }];
        const userMessageId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
        pinToBottom();
        setMessages((prev) => [...prev, { id: userMessageId, role: 'user', kind: 'text', content: prompt }]);
        setInput('');
        setInterpreting(true);
        setGeneratedState(null);

        try {
            const payload = {
                lang,
                messages: nextTranscript.slice(-MAX_TRANSCRIPT_MESSAGES_TO_SEND),
                active_draft: activeDraft ? {
                    req: activeDraft.req,
                    draft_metadata: activeDraft.draft_metadata || {},
                } : null,
            };
            const res = await interpretAiDraft(payload);

            if (res?.type === 'start_generation') {
                const actionText = String(res?.message || t(lang, 'genStarting')).trim();
                if (actionText) {
                    addMessage({ role: 'assistant', kind: 'text', content: actionText });
                    setTranscript([...nextTranscript, { role: 'assistant', content: actionText }]);
                } else {
                    setTranscript(nextTranscript);
                }
                await handleGenerate();
                return;
            }

            if (res?.type === 'clarification') {
                const clarification = String(res?.message || t(lang, 'clarifyFallback')).trim();
                addMessage({
                    role: 'assistant',
                    kind: 'text',
                    content: clarification,
                    quickReplies: Array.isArray(res?.quick_replies) ? res.quick_replies.slice(0, 4) : [],
                });
                setTranscript([...nextTranscript, { role: 'assistant', content: clarification }]);
                // Keep the last draft active: a clarification is just a chat answer
                // (e.g. "what is this?"), so its generator bar must stay available.
                return;
            }

            if (res?.type === 'criteria_draft') {
                const modelText = String(res?.message || '').trim();
                const summaryText = modelText || summarizeDraftText(res, lang);
                const draftMsgId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
                setMessages((prev) => [
                    ...prev,
                    {
                        id: draftMsgId,
                        role: 'assistant',
                        kind: 'draft',
                        content: summaryText,
                        draft: res,
                    },
                ]);
                setActiveDraftMessageId(draftMsgId);
                setTranscript([...nextTranscript, { role: 'assistant', content: summaryText }]);
                return;
            }

            const fallback = t(lang, 'clarifyFallback');
            addMessage({ role: 'assistant', kind: 'text', content: fallback });
            setTranscript([...nextTranscript, { role: 'assistant', content: fallback }]);
        } catch (err) {
            const status = Number(err?.response?.status || 0);
            const retryAfter = Number(err?.response?.data?.retry_after_seconds || 0);
            const serverError = err?.response?.data?.error || err?.message || '';

            let toastMsg;
            if (status === 429) {
                toastMsg = retryAfter > 0
                    ? `${t(lang, 'msgAiRateLimited')} (${retryAfter}s)`
                    : t(lang, 'msgAiRateLimited');
            } else if (status >= 500 || status === 0) {
                toastMsg = t(lang, 'msgAiUnavailable');
            } else {
                toastMsg = serverError || t(lang, 'msgAiInvalid');
            }

            setMessages((prev) => prev.filter((m) => m.id !== userMessageId));
            setInput(prompt);
            showNotification(toastMsg, NOTIFICATION.ERROR);
        } finally {
            setInterpreting(false);
        }
    };

    const handleStopGeneration = async () => {
        if (!generationJob.isGenerating) return;
        await generationJob.stop();
    };

    async function handleGenerate() {
        if (!activeDraft?.req) {
            showNotification(t(lang, 'msgDraftRequired'), NOTIFICATION.INFO);
            return;
        }
        if (generationJob.isGenerating) {
            await generationJob.stop();
            return;
        }

        const { build: baseBuild, genConfig } = aiDraftToBuild(activeDraft);
        const requestedTimeLimit = GenConfig.searchBudgetSeconds(
            Number(genConfig.timeLimit) || AI_CHAT_GENERATION_TIME_LIMIT_FALLBACK,
        );
        baseBuildRef.current = baseBuild;
        setGeneratedState(null);

        await generationJob.start({
            req: activeDraft.req,
            timeLimit: requestedTimeLimit,
            // The browser is the only solver: the routine the generator page
            // runs, on the build the draft describes. Without it every chat
            // generation declined and was reported as a browser that cannot
            // generate.
            attemptLocal: ({ signal, onLocalStart, onSearchStart, onFinalize, onFallback }) => solveBuild(baseBuild, genConfig, {
                seconds: Number(genConfig.timeLimit) || AI_CHAT_GENERATION_TIME_LIMIT_FALLBACK,
                signal,
                onFallback,
                onStart: onLocalStart,
                onSearching: onSearchStart,
                onFinalize,
            }),
            onDone: (jobRow) => {
                const solverBuild = jobRow?.result?.build;
                if (!solverBuild) {
                    showNotification(t(lang, 'msgNoResult'), NOTIFICATION.ERROR);
                    addMessage({ role: 'assistant', kind: 'text', content: t(lang, 'msgNoResult') });
                    return;
                }
                const built = applySolverBuildPayload({
                    solverBuildPayload: solverBuild,
                    baseBuild: baseBuildRef.current,
                });
                if (!built) {
                    showNotification(t(lang, 'msgGenFailed'), NOTIFICATION.ERROR);
                    addMessage({ role: 'assistant', kind: 'text', content: t(lang, 'msgGenFailed') });
                    return;
                }
                const solverResult = jobRow?.result || {};
                setGeneratedState({ build: built, genConfig, solverResult });
                const userRequest = userRequestForMessage(activeDraftMessageId);
                addMessage({
                    role: 'assistant',
                    kind: 'build',
                    content: t(lang, 'genDone'),
                    build: built,
                    genConfig,
                    solverResult,
                    draft: activeDraft,
                    userRequest,
                });
                // Feed the model an authoritative listing of what was generated
                // so it can answer slot questions and reference item ids on the
                // next turn. Distinct from the UI card text above.
                const transcriptSummary = serializeBuildForTranscript({
                    build: built,
                    draft: activeDraft,
                    lang,
                    userRequest,
                });
                if (transcriptSummary) {
                    setTranscript((prev) => [
                        ...prev,
                        { role: 'assistant', content: transcriptSummary },
                    ]);
                }
            },
            onError: (err) => {
                const status = Number(err?.response?.status || err?.status || 0);
                const retryAfter = Number(err?.retryAfterSeconds || err?.response?.data?.retry_after_seconds || 0);
                let msg = err?.response?.data?.error || err?.message || t(lang, 'msgGenFailed');
                if (status === 429) {
                    msg = retryAfter > 0
                        ? `${t(lang, 'msgGenRateLimited')} (${retryAfter}s)`
                        : t(lang, 'msgGenRateLimited');
                }
                showNotification(msg, NOTIFICATION.ERROR);
                addMessage({ role: 'assistant', kind: 'text', content: msg });
            },
        });
    }

    const userRequestForMessage = (messageId) => {
        const idx = messages.findIndex((m) => m.id === messageId);
        if (idx < 0) return '';
        for (let i = idx - 1; i >= 0; i -= 1) {
            if (messages[i].role === 'user') return messages[i].content || '';
        }
        return '';
    };

    // Append a marker line to the conversation transcript so the AI knows on
    // subsequent turns that this build was saved/opened. We persist directly
    // (in addition to setTranscript) because the caller navigates away
    // immediately after, which can pre-empt the saveConversation effect.
    const recordBuildOpenedInTranscript = ({ build, draft, buildId, userRequest }) => {
        const marker = serializeBuildOpenedMarker({
            build,
            draft,
            lang,
            buildId,
            userRequest,
        });
        if (!marker) return;
        const nextTranscript = [
            ...transcript,
            { role: 'assistant', content: marker },
        ];
        setTranscript(nextTranscript);
        if (conversationIdRef.current) {
            saveConversation({
                id: conversationIdRef.current,
                messages,
                transcript: nextTranscript,
                lang,
            });
        }
    };

    const handleOpenBuild = async (message) => {
        const build = message?.build || generatedState?.build;
        const genConfig = message?.genConfig || generatedState?.genConfig;
        const solverResult = message?.solverResult || generatedState?.solverResult || {};
        const draft = message?.draft || activeDraft;
        if (!build || !genConfig || !draft) return;
        if (openBusy) return;

        const anchorId = message?.id || activeDraftMessageId;
        const userRequest = userRequestForMessage(anchorId);

        setOpenBusy(true);
        try {
            const savedPayload = aiDraftToSavedBuildPayload({
                build,
                genConfig,
                name: suggestAiBuildName({ draft, lang, userRequest }),
            });

            if (isAuthenticated) {
                const saved = await createRemoteBuild(savedPayload);
                showNotification(t(lang, 'msgBuildSaved'), NOTIFICATION.SUCCESS);
                const remoteId = saved?.publicCode || saved?.id || null;
                recordBuildOpenedInTranscript({
                    build,
                    draft,
                    buildId: remoteId,
                    userRequest,
                });
                if (saved?.owner?.public_slug && saved?.publicCode) {
                    preApplyRemoteEntry(saved);
                    navigate(canonicalBuildPath(lang, saved.owner.public_slug, saved.publicCode));
                    return;
                }
                navigate(localGenPath(lang));
                return;
            }

            if (isLocalDevEnvironment()) {
                const entry = buildStorage.createBuildFromSavedPayload(savedPayload);
                showNotification(t(lang, 'msgBuildSaved'), NOTIFICATION.SUCCESS);
                recordBuildOpenedInTranscript({
                    build,
                    draft,
                    buildId: entry?.id || null,
                    userRequest,
                });
                navigate(localGenPath(lang));
                return;
            }

            showNotification(t(lang, 'msgClaimStart'), NOTIFICATION.INFO);
            const claim = await createAiBuildClaim({
                req: draft.req,
                draft_metadata: draft.draft_metadata || {},
                build_payload: savedPayload.build,
                gen_config: savedPayload.genConfig,
                solver_result: solverResult,
                name: savedPayload.name,
            });
            const token = claim?.claim_token;
            if (!token) throw new Error('claim token missing');
            const nextPath = `/${lang}/ai/claim?token=${encodeURIComponent(token)}`;
            window.location.assign(buildDiscordLoginUrl(nextPath));
        } catch (err) {
            const msg = err?.response?.data?.error || err?.message || t(lang, 'msgGenFailed');
            showNotification(msg, NOTIFICATION.ERROR);
        } finally {
            setOpenBusy(false);
        }
    };

    return (
        <main className="home-hero column">
            <section className="home-intro-section">
                <section className="ai-chat-panel has-chat">

                    <NavLink to={`/${lang}`} className="ai-chat-back-btn">
                        <West fontSize="small" />
                        {t(lang, 'back')}
                    </NavLink>

                    <div className="ai-chat-gen-promo">
                        <p>{t(lang, 'genPromo')}</p>
                        <Link to={localGenPath(lang)} className="ai-chat-cta-link">
                            <button type="button" className="primary alt">{t(lang, 'genPromoCta')} <East fontSize="small" /></button>
                        </Link>
                    </div>

                    <div className="ai-chat-messages-wrap">
                        <div className="ai-chat-messages">
                            <div className="ai-chat-empty-logo" aria-hidden="true">
                                <img src="/img/dofgen_logo.svg" alt="" />
                            </div>
                            {messages.map((message) => {
                                const isDraft = message.kind === 'draft' && message.draft;
                                const isBuild = message.kind === 'build' && message.build;
                                const isActiveDraft = isDraft && message.id === activeDraftMessageId;
                                const chipsDisabled = !isActiveDraft || generationJob.isGenerating;
                                const messageTimeLimit = isDraft
                                    ? draftTimeLimit(message.draft)
                                    : AI_CHAT_GENERATION_TIME_LIMIT_FALLBACK;
                                return (
                                    <div
                                        key={message.id}
                                        className={`ai-chat-message ai-chat-message-${message.role}`}
                                    >
                                        <MarkdownMessage content={message.content} lang={lang} />

                                        {isDraft && (
                                            <>
                                                <AiChatDraftChips
                                                    draft={message.draft}
                                                    isActiveDraft={isActiveDraft}
                                                    chipsDisabled={chipsDisabled}
                                                    timeLimit={messageTimeLimit}
                                                    onLevelChange={setDraftLevel}
                                                    onStatChange={setDraftStatValue}
                                                    onStatRemove={removeDraftStat}
                                                    onSpellRemove={removeDraftSpell}
                                                    onExoChange={setDraftExo}
                                                    onTimeLimitChange={setDraftTimeLimit}
                                                    onGenConfigChange={setDraftGenConfig}
                                                    generationJob={generationJob}
                                                    interpreting={interpreting}
                                                    openBusy={openBusy}
                                                    onGenerate={handleGenerate}
                                                    onStop={handleStopGeneration}
                                                    lang={lang}
                                                />
                                                {(message.draft?.assumptions || []).length > 0 && (
                                                    <div className="ai-chat-list-block">
                                                        <strong>{t(lang, 'assumptions')}</strong>
                                                        <ul>
                                                            {message.draft.assumptions.map((line) => (
                                                                <li key={line}>{line}</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                                {(message.draft?.draft_metadata?.warnings || []).length > 0 && (
                                                    <div className="ai-chat-list-block">
                                                        <strong>{t(lang, 'warnings')}</strong>
                                                        <ul>
                                                            {message.draft.draft_metadata.warnings.map((warn) => (
                                                                <li key={warn.code || warn.message}>
                                                                    {warn.message || warn.code}
                                                                </li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                                {isActiveDraft && !generationJob.isGenerating && generationJob.error && (
                                                    <p className="ai-chat-status">{generationJob.error}</p>
                                                )}
                                            </>
                                        )}

                                        {isBuild && (
                                            <AiChatBuildCard
                                                message={message}
                                                lang={lang}
                                                onOpen={handleOpenBuild}
                                                openBusy={openBusy}
                                                disabled={generationJob.isGenerating || openBusy}
                                            />
                                        )}

                                        {Array.isArray(message.quickReplies) && message.quickReplies.length > 0 && (
                                            <div className="ai-chat-quick-replies">
                                                {message.quickReplies.map((reply) => (
                                                    <button
                                                        key={`${message.id}-${reply}`}
                                                        type="button"
                                                        className="secondary"
                                                        onClick={() => sendPrompt(reply)}
                                                    >
                                                        {reply}
                                                    </button>
                                                ))}
                                            </div>
                                        )}
                                    </div>
                                );
                            })}
                            {interpreting && (
                                <div className="ai-chat-message ai-chat-message-assistant">
                                    <span
                                        className="ai-chat-typing"
                                        role="status"
                                        aria-label={t(lang, 'aiBusy')}
                                    >
                                        <span></span>
                                        <span></span>
                                        <span></span>
                                    </span>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="ai-chat-input-row">
                        <div className="ai-chat-input-shell">
                            <input
                                ref={inputRef}
                                value={input}
                                onChange={(e) => setInput(e.target.value)}
                                placeholder={t(lang, 'placeholder')}
                                onKeyDown={(e) => {
                                    if (e.key === 'Enter' && !e.shiftKey) {
                                        e.preventDefault();
                                        sendPrompt(input);
                                    }
                                }}
                            />
                            <button
                                type="button"
                                className="ai-chat-send-btn primary"
                                onClick={() => sendPrompt(input)}
                                disabled={!input.trim() || interpreting || generationJob.isGenerating}
                                aria-label={t(lang, 'send')}
                            >
                                <East fontSize="small" />
                            </button>
                        </div>
                    </div>
                </section>
            </section>
        </main>
    );
};

export default Chat;
